import { Search } from "lucide-react";
import type { FormEvent, ReactNode } from "react";
import { useState } from "react";

// Direct-test entry for Device 360. Normally this surface is reached via global search / links, but
// a serial box makes it independently testable: type a serial → navigate to /device/:serial.

export interface DevSerialInputProps {
  readonly onSubmit: (serial: string) => void;
  readonly examples?: readonly string[];
  readonly current?: string;
}

export const DevSerialInput = ({
  onSubmit,
  examples = [],
  current,
}: DevSerialInputProps): ReactNode => {
  const [value, setValue] = useState(current ?? "");

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    const trimmed = value.trim();
    if (trimmed.length > 0) {
      onSubmit(trimmed);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 10,
        alignItems: "center",
      }}
    >
      <form onSubmit={submit} style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <label
          htmlFor="dev-serial"
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            padding: "8px 12px",
            background: "var(--br-surface-2)",
            border: "1px solid var(--br-border)",
            borderRadius: "var(--br-radius-md)",
          }}
        >
          <Search size={14} aria-hidden style={{ color: "var(--br-text-muted)" }} />
          <input
            id="dev-serial"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Enter device serial…"
            spellCheck={false}
            autoComplete="off"
            style={{
              background: "transparent",
              border: "none",
              outline: "none",
              color: "var(--br-text-primary)",
              fontFamily: "var(--br-font-mono)",
              fontSize: 13,
              width: 180,
            }}
          />
        </label>
        <button
          type="submit"
          style={{
            padding: "8px 14px",
            background: "var(--br-accent)",
            color: "var(--br-accent-contrast)",
            border: "none",
            borderRadius: "var(--br-radius-md)",
            fontWeight: 600,
            fontSize: 12,
            cursor: "pointer",
          }}
        >
          Look up
        </button>
      </form>

      {examples.length > 0 ? (
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, color: "var(--br-text-muted)" }}>try:</span>
          {examples.map((serial) => (
            <button
              key={serial}
              type="button"
              onClick={() => onSubmit(serial)}
              className="mono"
              style={{
                padding: "4px 8px",
                background: "var(--br-surface-2)",
                color: "var(--br-text-secondary)",
                border: "1px solid var(--br-border)",
                borderRadius: "var(--br-radius-sm)",
                fontSize: 11,
                cursor: "pointer",
              }}
            >
              {serial}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};
